import React, { useState, useEffect, useRef } from "react";
import { downloadCsv } from "../common/sharedFunctions";
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from "common";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import { makeStyles } from "@mui/styles";
import { 
  Modal, 
  Grid, 
  Typography, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from "@mui/material";
import { colors } from "../components/Theme/WebTheme";
import Button from "components/CustomButtons/Button.js";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningIcon from "@mui/icons-material/Warning";
import { AccountBalanceWallet as WalletIcon } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import AlertDialog from "../components/AlertDialog";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import { Autocomplete } from "@mui/material";
import WalletHistoryModal from "../components/WalletHistoryModal";

const useStyles = makeStyles((theme) => ({
    heading: {
        marginBottom: 20,
        height: "10%",
    },
    modal: {
        display: "flex",
        padding: theme.spacing(1),
        alignItems: "center",
        justifyContent: "center",
    },
    paper: {
        width: 500,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
        borderRadius: 15,
        alignItems: "center",
        justifyContent: "center",
    },
    imageContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50px",
        minWidth: "50px",
        backgroundColor: "#f5f5f5",
        borderRadius: "4px",
    },
    documentImageContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100px",
        minWidth: "100px",
        backgroundColor: "#f5f5f5",
        borderRadius: "4px",
        cursor: "pointer",
        border: "1px solid #ddd",
    },
    brokenImageText: {
        color: "#666",
        fontSize: "12px",
        textAlign: "center",
        padding: "5px",
    },
}));

// Enhanced Image Component with error handling
const ImageWithFallback = ({
    src,
    alt,
    style,
    onClick,
    containerStyle,
    showBrokenText = false,
}) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const classes = useStyles();
    const { t } = useTranslation();

    const handleImageError = () => {
        setImageError(true);
        setImageLoading(false);
    };

    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
    };

    if (!src) {
        return (
            <div className={classes.imageContainer} style={containerStyle}>
                <span className={classes.brokenImageText}>
                    {showBrokenText ? t("no_image") : ""}
                </span>
            </div>
        );
    }

    return (
        <div
            className={classes.imageContainer}
            style={containerStyle}
            onClick={onClick}
        >
            {imageLoading && <CircularProgress size={20} />}
            {!imageError && (
                <img
                    src={src}
                    alt={alt}
                    style={{
                        ...style,
                        display: imageLoading ? "none" : "block",
                    }}
                    onError={handleImageError}
                    onLoad={handleImageLoad}
                />
            )}
            {imageError && (
                <span className={classes.brokenImageText}>
                    {showBrokenText ? t("image_load_error") : "❌"}
                </span>
            )}
        </div>
    );
};

// Enhanced Document Image Component
const DocumentImageButton = ({ src, alt, onClick, style }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const classes = useStyles();
    const { t } = useTranslation();

    const handleImageError = () => {
        setImageError(true);
        setImageLoading(false);
    };

    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
    };

    if (!src) {
        return (
            <div className={classes.documentImageContainer} style={style}>
                <span className={classes.brokenImageText}>
                    {t("no_document")}
                </span>
            </div>
        );
    }

    return (
        <button
            onClick={onClick}
            style={{ border: "none", padding: 0, background: "none" }}
        >
            <div className={classes.documentImageContainer} style={style}>
                {imageLoading && <CircularProgress size={20} />}
                {!imageError && (
                    <img
                        src={src}
                        alt={alt}
                        style={{
                            width: 100,
                            height: "auto",
                            maxHeight: 80,
                            display: imageLoading ? "none" : "block",
                        }}
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                    />
                )}
                {imageError && (
                    <span className={classes.brokenImageText}>
                        {t("click_to_view")}
                    </span>
                )}
            </div>
        </button>
    );
};

export default function Users() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const {
        addUser,
        editUser,
        deleteUser,
        checkUserExists,
        fetchUsersOnce,
        updateLicenseImage,
    } = api;
    const [data, setData] = useState([]);
    const [cars, setCars] = useState({});
    const staticusers = useSelector((state) => state.usersdata.staticusers);
    const cartypes = useSelector((state) => state.cartypes);
    const categoriesList = useSelector(
        (state) => state.categorylistdata.categories
    );
    const auth = useSelector((state) => state.auth);
    const settings = useSelector((state) => state.settingsdata.settings);
    const dispatch = useDispatch();
    const { bulkUpdateDriversCategoryCarType } = api;
    const loaded = useRef(false);
    const classes = useStyles();
    const rootRef = useRef(null);
    const [role, setRole] = useState(null);
    const [fleetAdmins, setFleetAdmins] = useState([]);
    const [fleetAdminsObj, setFleetAdminsObj] = useState();
    const countries = useSelector((state) => state.countrylistdata.countries);
    const [countryList, setCountryList] = useState();
    const addons = useSelector((state) => state.addonsdata.addons);

    useEffect(() => {
        if (countries) {
            let list = {};
            for (let i = 0; i < countries.length; i++) {
                let obj = countries[i];
                list[obj.country] = obj.country;
            }
            setCountryList(list);
        }
    }, [countries]);

    useEffect(() => {
        dispatch(fetchUsersOnce());
    }, [dispatch, fetchUsersOnce]);

    useEffect(() => {
        if (staticusers) {
            if (role === "admin") {
                let arr = staticusers.filter(
                    (user) => user.usertype === "fleetadmin"
                );
                let obj = {};
                let arr2 = [];
                for (let i = 0; i < arr.length; i++) {
                    let user = arr[i];
                    arr2.push({
                        id: user.id,
                        desc:
                            user.firstName +
                            " " +
                            user.lastName +
                            " (" +
                            (settings.AllowCriticalEditsAdmin
                                ? user.mobile
                                : "Hidden") +
                            ") " +
                            (settings.AllowCriticalEditsAdmin
                                ? user.email
                                : "Hidden"),
                    });
                    obj[user.id] =
                        user.firstName +
                        " " +
                        user.lastName +
                        " (" +
                        (settings.AllowCriticalEditsAdmin
                            ? user.mobile
                            : "Hidden") +
                        ") " +
                        (settings.AllowCriticalEditsAdmin
                            ? user.email
                            : "Hidden");
                }
                setFleetAdmins(arr2);
                setFleetAdminsObj(obj);
            }
            setTimeout(() => {
                setData(
                    staticusers.filter(
                        (user) =>
                            user.usertype === "driver" &&
                            ((user.fleetadmin === auth.profile.uid &&
                                auth.profile.usertype === "fleetadmin") ||
                                auth.profile.usertype === "admin")
                    )
                );
            }, 1000);
        } else {
            setData([]);
        }
        loaded.current = true;
    }, [
        staticusers,
        auth.profile.usertype,
        auth.profile.uid,
        settings.AllowCriticalEditsAdmin,
        role,
    ]);

    useEffect(() => {
        if (auth.profile && auth.profile.usertype) {
            setRole(auth.profile.usertype);
        }
    }, [auth.profile]);

    useEffect(() => {
        if (cartypes.cars) {
            let obj = {};
            cartypes.cars.map((car) => (obj[car.name] = car.name));
            setCars(obj);
        }
    }, [cartypes.cars]);

    const [imageType, setImageType] = useState(null);

    const onClick = (rowData, text) => {
        if (text === "licenseImage") {
            setImageType(text);
            setImageData(rowData.licenseImage);
        } else if (text === "licenseImageBack") {
            setImageType(text);
            setImageData(rowData.licenseImageBack);
        } else if (text === "verifyIdImage") {
            setImageType(text);
            setImageData(rowData.verifyIdImage);
        }
        setProfileModal(true);
        setUserData(rowData);
    };

    const columns = [
        {
            title: t("createdAt"),
            field: "createdAt",
            editable: "never",
            defaultSort: "desc",
            render: (rowData) =>
                rowData.createdAt
                    ? moment(rowData.createdAt).format("lll")
                    : null,
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("first_name"),
            field: "firstName",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("last_name"),
            field: "lastName",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("mobile"),
            field: "mobile",
            editable: "onAdd",
            render: (rowData) =>
                settings.AllowCriticalEditsAdmin
                    ? rowData.mobile
                    : "Hidden for Demo",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("email"),
            field: "email",
            editable: "onAdd",
            render: (rowData) =>
                settings.AllowCriticalEditsAdmin
                    ? rowData.email
                    : "Hidden for Demo",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
            headerStyle: { textAlign: "center" },
        },
        {
            title: t("profile_image"),
            field: "profile_image",
            render: (rowData) => (
                <ImageWithFallback
                    src={rowData.profile_image}
                    alt="Profile"
                    style={{
                        width: 50,
                        height: 50,
                        borderRadius: "50%",
                        objectFit: "cover",
                    }}
                    containerStyle={{
                        width: 50,
                        height: 50,
                        borderRadius: "50%",
                    }}
                    showBrokenText={false}
                />
            ),
            editable: "never",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("country"),
            field: "country",
            lookup: countryList,
            editable: "onAdd",
        },
        {
            title: t("verify_id"),
            field: "verifyId",
            cellStyle: { textAlign: isRTL === "rtl" ? "center" : "left" },
        },
        {
            title: t("verifyid_image"),
            field: "verifyIdImage",
            cellStyle: { textAlign: "center" },
            render: (rowData) => (
                <DocumentImageButton
                    src={rowData.verifyIdImage}
                    alt="Verify ID"
                    onClick={() => onClick(rowData, "verifyIdImage")}
                />
            ),
        },
        {
            title: t("fleetadmins"),
            field: "fleetadmin",
            hidden: role === "admin" ? false : true,
            editable: role === "admin" ? "always" : "never",
            cellStyle: { textAlign: "center" },
            render: (rowData) =>
                rowData.fleetadmin ? fleetAdminsObj[rowData.fleetadmin] : null,
            editComponent: (props) => (
                <Autocomplete
                    id="combo-box-demo"
                    options={fleetAdmins}
                    getOptionLabel={(option) => option.desc}
                    style={{ width: 300 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Combo box"
                            variant="outlined"
                        />
                    )}
                    onChange={(event, newValue) => {
                        props.onChange(newValue.id);
                    }}
                    disableClearable={true}
                />
            ),
        },
        {
            title: t("vehicle_model_name"),
            field: "vehicleMake",
            editable: "never",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("vehicle_model_no"),
            field: "vehicleModel",
            editable: "never",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("vehicle_year"),
            field: "vehicleYear",
            editable: "never",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("vehicle_color"),
            field: "vehicleColor",
            editable: "never",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("vehicle_reg_no"),
            field: "vehicleNumber",
            editable: "never",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("other_info"),
            field: "other_info",
            editable: "never",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("car_type"),
            field: "carType",
            editable: "never",
            lookup: cars,
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("category"),
            field: "category",
            editable: "never",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
            render: (rowData) => {
                return rowData.category ? t(rowData.category) : null;
            },
        },
        {
            title: t("car_approval"),
            field: "carApproved",
            editable: "never",
            type: "boolean",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("account_approve"),
            field: "approved",
            type: "boolean",
            initialEditValue: true,
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("driver_active"),
            field: "driverActiveStatus",
            type: "boolean",
            initialEditValue: true,
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("license_image_front"),
            field: "licenseImage",
            cellStyle: { textAlign: "center" },
            render: (rowData) => (
                <DocumentImageButton
                    src={rowData.licenseImage}
                    alt="License Front"
                    onClick={() => onClick(rowData, "licenseImage")}
                />
            ),
        },
        {
            title: t("license_image_back"),
            field: "licenseImageBack",
            cellStyle: { textAlign: "center" },
            render: (rowData) => (
                <DocumentImageButton
                    src={rowData.licenseImageBack}
                    alt="License Back"
                    onClick={() => onClick(rowData, "licenseImageBack")}
                />
            ),
        },
        {
            title: t("wallet_balance"),
            field: "walletBalance",
            type: "numeric",
            editable: "never",
            initialEditValue: 0,
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
            render: (rowData) => (
                <span
                    style={{
                        fontWeight: "bold",
                        color:
                            parseFloat(rowData.walletBalance || 0) > 0
                                ? "#4caf50"
                                : "#757575",
                    }}
                >
                    {settings.symbol}
                    {parseFloat(rowData.walletBalance || 0).toFixed(2)}
                </span>
            ),
        },
        ...(addons?.showWalletHistory
            ? [
                  {
                      title: t("history"),
                      field: "history",
                      type: "numeric",
                      editable: "never",
                      initialEditValue: 0,
                      cellStyle: {
                          textAlign: isRTL === "rtl" ? "right" : "center",
                      },
                      render: (rowData) => (
                          <IconButton
                              onClick={() => {
                                  setSelectedUser(rowData);
                                  setWalletHistoryModal(true);
                              }}
                          >
                              <WalletIcon />
                          </IconButton>
                      ),
                  },
              ]
            : []),
        {
            title: t("you_rated_text"),
            render: (rowData) => (
                <span>{rowData.rating ? rowData.rating : "0"}</span>
            ),
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("signup_via_referral"),
            field: "signupViaReferral",
            editable: "never",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("referralId"),
            field: "referralId",
            editable: "never",
            initialEditValue: "",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
        {
            title: t("stripe_customer_id"),
            field: "stripe_customer_id",
            editable: "never",
            render: (rowData) =>
                settings.AllowCriticalEditsAdmin
                    ? rowData.stripe_customer_id
                    : "Hidden for Demo",
        },
        {
            title: t("stripe_account_id"),
            field: "stripe_account_id",
            editable: "never",
            render: (rowData) =>
                settings.AllowCriticalEditsAdmin
                    ? rowData.stripe_account_id
                    : "Hidden for Demo",
        },
        {
            title: t("paypal_email"),
            field: "paypal_email",
            editable: "never",
            render: (rowData) =>
                settings.AllowCriticalEditsAdmin
                    ? rowData.paypal_email
                    : "Hidden for Demo",
        },
        {
            title: t("queue"),
            field: "queue",
            type: "boolean",
            initialEditValue: false,
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "center" },
        },
    ];

    const [profileModal, setProfileModal] = useState(false);
    const [walletHistoryModal, setWalletHistoryModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [imageData, setImageData] = useState(false);
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
    const [loading, setLoading] = useState(false);

    const [selectedImage, setSelectedImage] = useState(null);
    const handleProfileModal = (e) => {
        setProfileModal(false);
        setSelectedImage(null);
    };

    const [userData, setUserData] = useState();
    const documentUsers = useSelector((state) => state.documentUsersData);
    const documentFields = useSelector((state) => state.documentFieldsData);
    
    // Modal for document validation errors
    const [documentErrorModal, setDocumentErrorModal] = useState({
        open: false,
        title: '',
        message: '',
        missingDocs: []
    });

    // ✅ Function to check if all required documents are approved
    const checkAllDocumentsApproved = async (userId, userType) => {
        try {
            console.log('🔍 ========================================');
            console.log('🔍 CHECKING DOCUMENTS FOR APPROVAL');
            console.log('🔍 User ID:', userId);
            console.log('🔍 User Type:', userType);
            console.log('🔍 Document Fields State:', documentFields);
            console.log('🔍 Document Users State:', documentUsers);
            console.log('🔍 ========================================');

            // Check if data is loaded
            if (!documentFields || !documentFields.data) {
                console.warn('⚠️ Document fields data not loaded yet');
                return { 
                    canApprove: false, 
                    message: t('document_data_loading') || 'Document data is loading. Please try again in a moment.' 
                };
            }

            if (!documentUsers || !documentUsers.data) {
                console.warn('⚠️ Document users data not loaded yet');
                return { 
                    canApprove: false, 
                    message: t('document_data_loading') || 'Document data is loading. Please try again in a moment.' 
                };
            }

            // Get all required document fields for this user type
            const allFields = Object.values(documentFields.data);
            console.log('📋 Total document fields:', allFields.length);
            
            const requiredFields = allFields.filter(
                field => {
                    const isActive = field.state === true;
                    const isRequired = field.required === true;
                    const isForUserType = field.for_user === userType || field.for_user === 'both';
                    const included = isActive && isRequired && isForUserType;
                    
                    console.log(`📄 Field: ${field.name}`, {
                        state: field.state,
                        required: field.required,
                        for_user: field.for_user,
                        isActive,
                        isRequired,
                        isForUserType,
                        '✅ INCLUDED': included
                    });
                    
                    return included;
                }
            );

            console.log('📋 Required document fields:', requiredFields.length);
            console.log('📋 Required fields:', requiredFields.map(f => f.name));

            if (requiredFields.length === 0) {
                // No required documents, can approve
                console.log('✅ No required documents for this user type');
                return { canApprove: true, message: '' };
            }

            // Get user's submitted documents
            const allUserDocs = Object.values(documentUsers.data);
            console.log('📄 Total user documents in system:', allUserDocs.length);
            
            const userDocuments = allUserDocs.filter(doc => {
                const match = doc.user_id === userId;
                if (match) {
                    console.log('📄 Found user document:', {
                        id: doc.id,
                        field_id: doc.field_id,
                        field_name: doc.field?.name,
                        approved: doc.approved,
                        status: doc.status,
                        user_id: doc.user_id
                    });
                }
                return match;
            });

            console.log('📄 User submitted documents:', userDocuments.length);

            // Check each required field
            const missingOrPendingDocs = [];
            
            for (const field of requiredFields) {
                console.log(`🔍 Checking required field: ${field.name} (ID: ${field.id})`);
                
                const userDoc = userDocuments.find(doc => doc.field_id === field.id);
                
                if (!userDoc) {
                    // Document not uploaded yet
                    console.log(`❌ Document NOT FOUND for field: ${field.name}`);
                    missingOrPendingDocs.push({
                        name: field.name,
                        status: 'not_uploaded',
                        reason: t('document_not_uploaded') || 'Not uploaded'
                    });
                } else {
                    console.log(`📄 Document found for ${field.name}:`, {
                        approved: userDoc.approved,
                        status: userDoc.status
                    });
                    
                    if (userDoc.approved !== true || userDoc.status !== 'approved') {
                        // Document uploaded but not approved yet
                        console.log(`❌ Document NOT APPROVED for field: ${field.name}`);
                        missingOrPendingDocs.push({
                            name: field.name,
                            status: userDoc.status || 'pending',
                            reason: userDoc.status === 'rejected' 
                                ? (userDoc.reason || t('document_rejected') || 'Rejected')
                                : (t('document_pending_approval') || 'Pending approval')
                        });
                    } else {
                        console.log(`✅ Document APPROVED for field: ${field.name}`);
                    }
                }
            }

            if (missingOrPendingDocs.length > 0) {
                const message = `${t('cannot_approve_driver_documents') || 'Cannot approve driver. The following documents are not approved'}:\n\n${
                    missingOrPendingDocs.map(doc => `• ${doc.name}: ${doc.reason}`).join('\n')
                }`;
                
                console.log('❌ ========================================');
                console.log('❌ DOCUMENTS CHECK FAILED');
                console.log('❌ Missing/Pending documents:', missingOrPendingDocs);
                console.log('❌ ========================================');
                return { canApprove: false, message, missingDocs: missingOrPendingDocs };
            }

            console.log('✅ ========================================');
            console.log('✅ ALL REQUIRED DOCUMENTS ARE APPROVED');
            console.log('✅ ========================================');
            return { canApprove: true, message: '' };

        } catch (error) {
            console.error('❌ Error checking documents:', error);
            console.error('❌ Error stack:', error.stack);
            return { 
                canApprove: false, 
                message: t('error_checking_documents') || 'Error checking documents. Please try again.' 
            };
        }
    };

    const handleCommonAlertClose = (e) => {
        e.preventDefault();
        setCommonAlert({ open: false, msg: "" });
    };

    const handleSetProfileModal = (e) => {
        e.preventDefault();
        if (selectedImage) {
            setLoading(true);
            dispatch(updateLicenseImage(userData.id, selectedImage, imageType));
            setProfileModal(false);
            setSelectedImage(null);
            setTimeout(() => {
                setLoading(false);
                dispatch(fetchUsersOnce());
            }, 10000);
        } else {
            setCommonAlert({ open: true, msg: t("choose_image_first") });
        }
    };

    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkCategory, setBulkCategory] = useState(null);
    const [bulkCarType, setBulkCarType] = useState(null);

    const categoryOptions = (categoriesList || [])
        .filter((c) => c && c.active)
        .map((c) => ({ label: t(c.category), value: c.category }));

    const carTypeOptions = (cartypes.cars || [])
        .filter((c) => c.active && c.category === bulkCategory)
        .map((c) => ({ label: t(c.name), value: c.name }));

    return !loaded.current ? (
        <CircularLoading />
    ) : (
        <div style={{ backgroundColor: colors.WHITE }}>
            <MaterialTable
                title={t("drivers")}
                columns={columns}
                style={{
                    direction: isRTL === "rtl" ? "rtl" : "ltr",
                    borderRadius: "8px",
                    boxShadow: "4px 4px 6px #9E9E9E",
                }}
                data={data}
                onRowClick={(evt, selectedRow) =>
                    setSelectedRow(selectedRow.tableData.id)
                }
                onSelectionChange={(rows) => setSelectedRows(rows)}
                options={{
                    selection: settings.categories_and_services_bulk_change
                        ? true
                        : false,
                    exportCsv: (columns, data) => {
                        let hArray = [];
                        const headerRow = columns.map((col) => {
                            if (typeof col.title === "object") {
                                return col.title.props.text;
                            }
                            hArray.push(col.field);
                            return col.title;
                        });
                        const dataRows = data.map(({ tableData, ...row }) => {
                            row.createdAt =
                                new Date(row.createdAt).toLocaleDateString() +
                                " " +
                                new Date(row.createdAt).toLocaleTimeString();
                            row.fleetadmin = row.fleetadmin
                                ? fleetAdminsObj[row.fleetadmin]
                                : "";
                            let dArr = [];
                            for (let i = 0; i < hArray.length; i++) {
                                dArr.push(row[hArray[i]]);
                            }
                            return Object.values(dArr);
                        });
                        const { exportDelimiter } = ",";
                        const delimiter = exportDelimiter
                            ? exportDelimiter
                            : ",";
                        const csvContent = [headerRow, ...dataRows]
                            .map((e) => e.join(delimiter))
                            .join("\n");
                        const csvFileName = "download.csv";
                        downloadCsv(csvContent, csvFileName);
                    },
                    exportButton: {
                        csv: settings.AllowCriticalEditsAdmin,
                        pdf: false,
                    },
                    maxColumnSort: "all_columns",
                    rowStyle: (rowData) => ({
                        backgroundColor:
                            selectedRow === rowData.tableData.id
                                ? colors.THIRDCOLOR
                                : colors.WHITE,
                    }),
                    editable: {
                        backgroundColor: colors.WHITE,
                        fontSize: "0.8em",
                        fontWeight: "bold ",
                        fontFamily: 'Lucida Console", "Courier New", monospace',
                    },
                    headerStyle: {
                        position: "sticky",
                        top: "0px",
                        backgroundColor: colors.SECONDARY,
                        color: colors.WHITE,
                        fontSize: "0.8em",
                        fontWeight: "bold ",
                        fontFamily: 'Lucida Console", "Courier New", monospace',
                    },
                }}
                localization={{
                    body: {
                        addTooltip: t("add"),
                        deleteTooltip: t("delete"),
                        editTooltip: t("edit"),
                        emptyDataSourceMessage: t("blank_message"),
                        editRow: {
                            deleteText: t("delete_message"),
                            cancelTooltip: t("cancel"),
                            saveTooltip: t("save"),
                        },
                    },
                    toolbar: {
                        searchPlaceholder: t("search"),
                        exportTitle: t("export"),
                        nRowsSelected: "{0} " + t("rows_selected"),
                    },
                    header: {
                        actions: t("actions"),
                    },
                    pagination: {
                        labelDisplayedRows:
                            "{from}-{to} " + t("of") + " {count}",
                        firstTooltip: t("first_page_tooltip"),
                        previousTooltip: t("previous_page_tooltip"),
                        nextTooltip: t("next_page_tooltip"),
                        lastTooltip: t("last_page_tooltip"),
                    },
                }}
                editable={{
                    onRowAdd: (newData) =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                checkUserExists(newData).then((res) => {
                                    if (res.users && res.users.length > 0) {
                                        alert(t("user_exists"));
                                        reject();
                                    } else if (
                                        !(newData && newData.firstName)
                                    ) {
                                        alert(t("proper_input_name"));
                                        reject();
                                    } else if (res.error) {
                                        alert(t("email_or_mobile_issue"));
                                        reject();
                                    } else if (
                                        settings &&
                                        settings.license_image_required &&
                                        (newData.licenseImage === "" ||
                                            !newData.licenseImage)
                                    ) {
                                        alert(t("proper_input_licenseimage"));
                                        reject();
                                    } else if (
                                        settings &&
                                        settings.license_image_required &&
                                        (newData.licenseImageBack === "" ||
                                            !newData.licenseImageBack)
                                    ) {
                                        alert(t("proper_input_licenseimage"));
                                        reject();
                                    } else if (
                                        settings &&
                                        settings.imageIdApproval &&
                                        (newData.verifyIdImage === "" ||
                                            !newData.verifyIdImage)
                                    ) {
                                        alert(t("upload_id_details"));
                                        reject();
                                    } else if (
                                        settings &&
                                        settings.imageIdApproval &&
                                        !newData.verifyId
                                    ) {
                                        alert(t("upload_id_details"));
                                        reject();
                                    } else if (!newData.country) {
                                        alert(t("country_blank_error"));
                                        reject();
                                    } else {
                                        newData["usertype"] = "driver";
                                        newData["createdAt"] =
                                            new Date().getTime();
                                        const c = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                                        const reference = [...Array(5)]
                                            .map(
                                                (_) =>
                                                    c[
                                                        ~~(
                                                            Math.random() *
                                                            c.length
                                                        )
                                                    ]
                                            )
                                            .join("");
                                        newData["referralId"] = reference;
                                        let role = auth.profile.usertype;
                                        if (role === "fleetadmin") {
                                            newData["fleetadmin"] =
                                                auth.profile.uid;
                                        }
                                        dispatch(addUser(newData));
                                        dispatch(fetchUsersOnce());
                                        resolve();
                                    }
                                });
                            }, 600);
                        }),
                    onRowUpdate: async (newData, oldData) => {
                        console.log('🔵 ========================================');
                        console.log('🔵 ON ROW UPDATE TRIGGERED');
                        console.log('🔵 Old Data:', { id: oldData.id, firstName: oldData.firstName, approved: oldData.approved });
                        console.log('🔵 New Data:', { id: newData.id, firstName: newData.firstName, approved: newData.approved });
                        console.log('🔵 ========================================');

                        return new Promise(async (resolve, reject) => {
                            try {
                                // Basic validations
                                if (!(newData && newData.firstName)) {
                                    alert(t("proper_input_name"));
                                    reject();
                                    return;
                                }
                                
                                if (settings && settings.license_image_required && !(newData && newData.licenseImage)) {
                                    alert(t("proper_input_licenseimage"));
                                    reject();
                                    return;
                                }
                                
                                if (settings && settings.imageIdApproval && (newData.verifyIdImage === "" || !newData.verifyIdImage)) {
                                    alert(t("upload_id_details"));
                                    reject();
                                    return;
                                }
                                
                                if (settings && settings.imageIdApproval && !newData.verifyId) {
                                    alert(t("upload_id_details"));
                                    reject();
                                    return;
                                }

                                // ✅ VALIDASI DOCUMENTS: Check if trying to approve driver
                                if (newData.approved === true && oldData.approved === false) {
                                    console.log('🚨 ========================================');
                                    console.log('🚨 DRIVER APPROVAL ATTEMPT DETECTED');
                                    console.log('🚨 Old approved:', oldData.approved);
                                    console.log('🚨 New approved:', newData.approved);
                                    console.log('🚨 Driver ID:', newData.id);
                                    console.log('🚨 Driver Name:', newData.firstName, newData.lastName);
                                    console.log('🚨 ========================================');
                                    
                                    const docCheck = await checkAllDocumentsApproved(newData.id, 'driver');
                                    
                                    if (!docCheck.canApprove) {
                                        console.log('🚨 APPROVAL REJECTED - Documents not approved');
                                        
                                        // Show modal instead of alert
                                        setDocumentErrorModal({
                                            open: true,
                                            title: t('cannot_approve_driver') || 'Cannot Approve Driver',
                                            message: t('driver_missing_documents') || 'This driver has missing or pending documents that must be approved first.',
                                            missingDocs: docCheck.missingDocs || []
                                        });
                                        
                                        reject();
                                        return;
                                    }
                                    
                                    console.log('✅ All documents approved, proceeding with driver approval');
                                } else {
                                    console.log('ℹ️ Normal update (not approval change):', {
                                        oldApproved: oldData.approved,
                                        newApproved: newData.approved
                                    });
                                }

                                // Proceed with update
                                setTimeout(() => {
                                    resolve();
                                    if (newData !== oldData) {
                                        delete newData.tableData;
                                        dispatch(editUser(oldData.id, newData));
                                        dispatch(fetchUsersOnce());
                                    }
                                }, 600);

                            } catch (error) {
                                console.error('❌ Error in onRowUpdate:', error);
                                alert(t('update_failed') || 'Update failed. Please try again.');
                                reject(error);
                            }
                        });
                    },
                    onRowDelete: (oldData) =>
                        settings.AllowCriticalEditsAdmin
                            ? new Promise((resolve) => {
                                  setTimeout(() => {
                                      resolve();
                                      dispatch(deleteUser(oldData.id));
                                      dispatch(fetchUsersOnce());
                                  }, 600);
                              })
                            : new Promise((resolve) => {
                                  setTimeout(() => {
                                      resolve();
                                      alert(t("demo_mode"));
                                  }, 600);
                              }),
                }}
                actions={
                    settings.categories_and_services_bulk_change
                        ? [
                              {
                                  icon: () => (
                                      <div
                                          style={{
                                              display: "flex",
                                              alignItems: "center",
                                              flexWrap: "wrap",
                                          }}
                                      >
                                          <EditIcon />
                                          <Typography variant="subtitle2">
                                              {t("bulk_edit")}
                                          </Typography>
                                      </div>
                                  ),
                                  tooltip: t("bulk_edit"),
                                  onClick: () => {
                                      if (
                                          !selectedRows ||
                                          selectedRows.length === 0
                                      ) {
                                          setCommonAlert({
                                              open: true,
                                              msg: t("please_select_rows"),
                                          });
                                          return;
                                      }
                                      setBulkModalOpen(true);
                                  },
                              },
                          ]
                        : null
                }
            />
            <Modal
                open={loading}
                onClose={() => setLoading(false)}
                className={classes.modal}
                container={() => rootRef.current}
            >
                <CircularProgress
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: colors.WHITE,
                    }}
                />
            </Modal>
            <Modal
                disablePortal
                disableEnforceFocus
                disableAutoFocus
                open={profileModal}
                onClose={handleProfileModal}
                className={classes.modal}
                container={() => rootRef.current}
            >
                <Grid
                    container
                    spacing={1}
                    className={classes.paper}
                    style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
                >
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Typography component="h1" variant="h6">
                            {t("license_image")}
                            <input
                                type="file"
                                style={{ marginLeft: 10 }}
                                name={t("image")}
                                onChange={(event) => {
                                    setSelectedImage(event.target.files[0]);
                                }}
                            />
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        {selectedImage && !loading ? (
                            <Tooltip title={t("cancel")}>
                                <CancelIcon
                                    onClick={() => setSelectedImage(null)}
                                    style={{
                                        fontSize: 30,
                                        backgroundColor: colors.RED,
                                        borderRadius: 50,
                                        color: colors.WHITE,
                                    }}
                                />
                            </Tooltip>
                        ) : null}
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        {selectedImage ? (
                            <ImageWithFallback
                                src={URL.createObjectURL(selectedImage)}
                                alt="Selected Image"
                                style={{
                                    height: "200px",
                                    width: "auto",
                                    maxWidth: "100%",
                                }}
                                containerStyle={{
                                    marginTop: 10,
                                    minHeight: "200px",
                                    width: "100%",
                                }}
                                showBrokenText={false}
                            />
                        ) : (
                            <ImageWithFallback
                                src={imageData}
                                alt="Document Image"
                                style={{
                                    height: "200px",
                                    width: "auto",
                                    maxWidth: "100%",
                                }}
                                containerStyle={{
                                    marginTop: 10,
                                    minHeight: "200px",
                                    width: "100%",
                                }}
                                showBrokenText={true}
                            />
                        )}
                        <br />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        style={{
                            textAlign: isRTL === "rtl" ? "right" : "left",
                        }}
                    >
                        {loading ? (
                            <Grid
                                container
                                spacing={0}
                                alignItems="center"
                                justify="center"
                                style={{ minHeight: "5vh" }}
                            >
                                <CircularProgress />
                            </Grid>
                        ) : (
                            <Grid
                                item
                                xs={12}
                                sm={12}
                                md={12}
                                lg={12}
                                style={{
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                <Button
                                    onClick={handleProfileModal}
                                    variant="contained"
                                    color="danger"
                                >
                                    {t("cancel")}
                                </Button>
                                <Button
                                    onClick={handleSetProfileModal}
                                    variant="contained"
                                    color="success"
                                    style={{ marginLeft: 10 }}
                                >
                                    {t("save")}
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Modal>
            <AlertDialog
                open={commonAlert.open}
                onClose={handleCommonAlertClose}
            >
                {commonAlert.msg}
            </AlertDialog>
            <Modal
                disablePortal
                disableEnforceFocus
                disableAutoFocus
                open={bulkModalOpen}
                onClose={() => setBulkModalOpen(false)}
                className={classes.modal}
                container={() => rootRef.current}
            >
                <Grid
                    container
                    spacing={2}
                    className={classes.paper}
                    style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
                >
                    <Grid item xs={12}>
                        <Typography component="h1" variant="h6">
                            {t("bulk_edit")} — {t("drivers")} (
                            {selectedRows.length})
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={categoryOptions}
                            value={bulkCategory}
                            onChange={(e, v) =>
                                setBulkCategory(v ? v.value : null)
                            }
                            getOptionLabel={(option) => {
                                if (typeof option === "string") {
                                    const found = categoryOptions.find(
                                        (o) => o.value === option
                                    );
                                    return found ? found.label : option;
                                }
                                return option.label || "";
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("category")}
                                    variant="outlined"
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={carTypeOptions}
                            value={bulkCarType}
                            onChange={(e, v) =>
                                setBulkCarType(v ? v.value : null)
                            }
                            getOptionLabel={(option) => {
                                if (typeof option === "string") {
                                    const found = carTypeOptions.find(
                                        (o) => o.value === option
                                    );
                                    return found ? found.label : option;
                                }
                                return option.label || "";
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("car_type")}
                                    variant="outlined"
                                />
                            )}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        style={{
                            textAlign: isRTL === "rtl" ? "right" : "left",
                        }}
                    >
                        <Button
                            onClick={() => setBulkModalOpen(false)}
                            variant="contained"
                            color="danger"
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            onClick={() => {
                                if (
                                    !selectedRows ||
                                    selectedRows.length === 0
                                ) {
                                    setCommonAlert({
                                        open: true,
                                        msg: t("please_select_rows"),
                                    });
                                    return;
                                }
                                if (!bulkCategory && !bulkCarType) {
                                    setCommonAlert({
                                        open: true,
                                        msg: t("select_at_least_one_field"),
                                    });
                                    return;
                                }
                                const driverIds = selectedRows.map((r) => r.id);
                                setLoading(true);
                                dispatch(
                                    bulkUpdateDriversCategoryCarType(
                                        driverIds,
                                        bulkCategory,
                                        bulkCarType
                                    )
                                );
                                setBulkModalOpen(false);
                                setTimeout(() => {
                                    setLoading(false);
                                    dispatch(fetchUsersOnce());
                                }, 1000);
                            }}
                            variant="contained"
                            color="success"
                            style={{ marginLeft: 10 }}
                        >
                            {t("save")}
                        </Button>
                    </Grid>
                </Grid>
            </Modal>
            <AlertDialog
                open={commonAlert.open}
                onClose={handleCommonAlertClose}
            >
                {commonAlert.msg}
            </AlertDialog>
            <WalletHistoryModal
                open={walletHistoryModal}
                onClose={() => setWalletHistoryModal(false)}
                user={selectedUser}
            />
            
            {/* Document Validation Error Modal */}
            <Dialog
                open={documentErrorModal.open}
                onClose={() => setDocumentErrorModal({ ...documentErrorModal, open: false })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item>
                            <WarningIcon 
                                sx={{ 
                                    color: colors.RED, 
                                    fontSize: 32,
                                    verticalAlign: 'middle' 
                                }} 
                            />
                        </Grid>
                        <Grid item xs>
                            <Typography variant="h6" component="span">
                                {documentErrorModal.title}
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {documentErrorModal.message}
                    </Typography>
                    
                    {documentErrorModal.missingDocs && documentErrorModal.missingDocs.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                                {t('documents_required') || 'Required Documents'}:
                            </Typography>
                            <List dense>
                                {documentErrorModal.missingDocs.map((doc, index) => (
                                    <ListItem 
                                        key={index}
                                        sx={{ 
                                            backgroundColor: '#fff3e0',
                                            borderLeft: `4px solid ${colors.RED}`,
                                            mb: 1,
                                            borderRadius: 1
                                        }}
                                    >
                                        <ListItemIcon>
                                            <ErrorOutlineIcon sx={{ color: colors.RED }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={doc.name}
                                            secondary={doc.reason}
                                            primaryTypographyProps={{ fontWeight: 'medium' }}
                                            secondaryTypographyProps={{ color: 'error' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                    
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        {t('approve_documents_first') || 'Please approve all required documents in the Document Management page before approving this driver account.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDocumentErrorModal({ ...documentErrorModal, open: false })}
                        variant="contained"
                        color="primary"
                    >
                        {t('ok') || 'OK'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
